/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2016-2024 (original work) Open Assessment Technologies SA;
 */
import { union } from 'lodash';

/**
 * Ported from @oat-sa/tao-core-ui@3.3.0
 * https://github.com/oat-sa/tao-core-ui-fe/blob/v3.3.0/src/highlighter.js
 *
 * Changes:
 * - remove `clearOnClick` option.
 * - remove different model format for `keepEmptyNodes=false` (format with `inlineRanges`). Now model format is the same for all options.
 * - add `keepEmptyNodesIgnoreSelector` option
 * - add `containerElement` option
 * - add `getColorKeyForHighlight()` method
 * - add optional `color` parameter to `clearHighlights` method
 * - `clearHighlight()` method now returns Range which includes the content of removed highlight.
 * - instead of `className` option which acts as color and gives
 *        `<span class="hl-txt-blue">` markup for `className: "hl-txt-blue"` or `setColor('hl-txt-blue')`,
 *      there's now `baseClassName` option, and color is set as `data-color` attribute:
 *        `<span class="hl-txt" data-color="blue">` markup for `baseClassName: "hl-txt"` and `setColor('blue')`.
 */

/**
 * Data attribute used to logically group the wrapping nodes into a single selection
 * @type {string}
 */
const GROUP_ATTR = 'data-hl-group';

/**
 * Children of those nodes types cannot be highlighted
 * @type {string[]}
 */
const defaultBlackList = ['textarea', 'math', 'script', '.select2-container'];

/**
 * @param {Object} options
 * @param {String} options.baseClassName - name of the class that will be used by the wrappers tags to highlight text
 * @param {String} options.containerSelector - allows to select the root Node in which highlighting is allowed
 * @param {String} options.containerElement - instead of selector (for cases where root node itsn't attached to the document)
 * @param {Array<String>} [options.containersBlackList] - additional blacklist selectors to be added to module instance's blacklist
 * @param {Array<String>} [options.containersWhiteList] - whitelist selectors; supported only in `keepEmptyNodes` mode.
 *   Priority of blacklist or whitelist is decided by which selector is closest to the node. If no match found, node is considered whitelisted.
 * @param {Boolean} [options.keepEmptyNodes] - retain original dom structure as far as possible and do not remove empty nodes if they were not created by highlighter (may be needed on Svelte-generated markup, see https://github.com/sveltejs/svelte/issues/4423 and https://github.com/sveltejs/svelte/issues/6037)
 * @param {String} [options.keepEmptyNodesIgnoreSelector] - you may want to avoid that, in cases where text-node splitting and presence of empty nodes is unreliable and shouldn't influence highlights data model (e.g. when another editor is splitting/merging text nodes in parallel with this highlighter)
 * @param {String} [options.transientSelector] - when building/restoring model, assume these nodes are plain text nodes. Use case: highlights created by another highlighter instance. When restoring highlight: a) transient nodes may or may not be there; b) if transient node is there, highlight won't be created over it (like if blacklist). Supported only in `keepEmptyNodes=false/keepEmptyNodesIgnoreSelector=false` container, because counting split data of transient node is not implemented.
 * @returns {Object} - the highlighter instance
 */
export default function (options) {
    const baseClassName = options.baseClassName;
    const containerSelector = options.containerSelector;
    const keepEmptyNodes = options.keepEmptyNodes;
    const keepEmptyNodesIgnoreSelector = options.keepEmptyNodesIgnoreSelector;

    let activeColor = '';

    /**
     * list of node selectors which should NOT receive any highlighting from this instance
     * an optional passed-in blacklist is merged with local defaults
     * @type {Array}
     */
    const containersBlackList = union(defaultBlackList, options.containersBlackList);
    const containersBlackListSelector = containersBlackList.join(', ');
    let containersWhiteListSelector = null;
    let containersBlackAndWhiteListSelector = containersBlackListSelector;
    if (options.containersWhiteList) {
        containersWhiteListSelector = options.containersWhiteList.join(', ');
        containersBlackAndWhiteListSelector = union(containersBlackList, options.containersWhiteList).join(', ');
    }

    /**
     * used in recursive loops to decide if we should wrap or not the current node
     * @type {boolean}
     */
    let isWrapping = false;

    /**
     * performance improvement to break out of a potentially big recursive loop once the wrapping has ended
     * @type {boolean}
     */
    let hasWrapped = false;

    /**
     * used in recursive loops to assign a group Id to the current wrapped node
     * @type {number}
     */
    let currentGroupId;

    /**
     * Returns the node in which highlighting is allowed
     * @returns {Element}
     */
    function getContainer() {
        return document.querySelector(containerSelector) || options.containerElement;
    }

    /**
     * Returns all highlighted nodes, excluding any inside blacklisted elements
     * @returns {JQuery<HTMLElement>}
     */
    function getHighlightedNodes() {
        return Array.from(getContainer()?.querySelectorAll(`.${baseClassName}`) || []).filter(
            node => !isBlacklisted(node)
        );
    }

    /**
     * Returns all highlighted nodes for color, excluding any inside blacklisted elements
     * @param {String} color
     * @returns {JQuery<HTMLElement>}
     */
    function getHighlightedNodesForColor(color) {
        return Array.from(getContainer()?.querySelectorAll(`.${baseClassName}[data-color="${color}"]` || [])).filter(
            node => !isBlacklisted(node)
        );
    }

    /**
     * Attach data to wrapper node.
     * Use it when deleting this highlight to know if highlight content should be merged with neighbour text nodes or not.
     * Use it when building/restoring index to know if restored highlight content should be split off neighbour text node or not.
     * Needed to keep markup the same as it was before highlighting.
     * @param {HTMLElement} node
     * @param {Boolean} beforeWasSplit
     * @param {Boolean} afterWasSplit
     */
    function addSplitData(node, beforeWasSplit, afterWasSplit) {
        if (shouldAddSplitData(node)) {
            node.dataset.beforeWasSplit = beforeWasSplit;
            node.dataset.afterWasSplit = afterWasSplit;
        } else {
            node.dataset.beforeWasSplit = true;
            node.dataset.afterWasSplit = true;
        }
    }

    /**
     * Highlight all text nodes within each given range
     * @param {Range[]} ranges - array of ranges to highlight, may be given by the helper selector.getAllRanges()
     */
    function highlightRanges(ranges) {
        ranges.forEach(function (range) {
            let rangeInfos;

            if (isRangeValid(range)) {
                currentGroupId = getAvailableGroupId();

                // easy peasy: highlighting a plain text without any DOM nodes
                // NOTE: The condition checks the whole node content and not a selected content in a given range, that allows to wrap whitespace
                if (
                    isWrappable(range.commonAncestorContainer) &&
                    !isWrappingNode(range.commonAncestorContainer.parentNode)
                ) {
                    const wrapperNode = getWrapper(currentGroupId, activeColor);
                    const beforeWasSplit = range.startOffset > 0;
                    const afterWasSplit = range.endOffset < range.commonAncestorContainer.length;
                    rangeSurroundContentsNoEmptyNodes(range, wrapperNode);
                    addSplitData(wrapperNode, beforeWasSplit, afterWasSplit);
                } else if (
                    isWrappable(range.commonAncestorContainer) &&
                    isWrappingNode(range.commonAncestorContainer.parentNode) &&
                    getColor(range.commonAncestorContainer.parentNode) !== activeColor
                ) {
                    highlightContainerNodes(range.commonAncestorContainer, activeColor, range, currentGroupId);

                    // now the fun stuff: highlighting a mix of text and DOM nodes
                } else {
                    rangeInfos = {
                        startNode: isElement(range.startContainer)
                            ? range.startContainer.childNodes[range.startOffset]
                            : range.startContainer,
                        startNodeContainer: range.startContainer,
                        startOffset: range.startOffset,

                        endNode:
                            isElement(range.endContainer) && range.endOffset > 0
                                ? range.endContainer.childNodes[range.endOffset - 1]
                                : range.endContainer,
                        endNodeContainer: range.endContainer,
                        endOffset: range.endOffset,
                        commonRange: range
                    };

                    isWrapping = false;
                    hasWrapped = false;
                    wrapTextNodesInRange(range.commonAncestorContainer, rangeInfos);
                }
            }

            // clean up the markup after wrapping...
            normalizeIfAllowed(range.commonAncestorContainer);

            currentGroupId = 0;
            isWrapping = false;
            reindexGroups(getContainer());
            mergeAdjacentWrappingNodes(getContainer());
            unWrapEmptyHighlights();
        });
    }

    /**
     * Check if a range is valid
     * @param {Range} range
     * @returns {boolean}
     */
    function isRangeValid(range) {
        let rangeInContainer;
        try {
            rangeInContainer =
                getContainer().contains(range.commonAncestorContainer) ||
                getContainer() === range.commonAncestorContainer;

            return rangeInContainer && !range.collapsed;
        } catch (e) {
            return false;
        }
    }

    /**
     * Core wrapping function. Traverse the DOM tree and highlight (= wraps) all text nodes within the given range.
     * Recursive.
     *
     * @param {Node} rootNode - top of the node hierarchy in which text nodes will be searched
     * @param {Object} rangeInfos
     * @param {Node} rangeInfos.startNode - node on which the selection starts
     * @param {Node} rangeInfos.startNodeContainer - container of the startNode, or the start node itself in case of text nodes
     * @param {number} rangeInfos.startOffset - same as range.startOffset, but not read-only to allow override
     * @param {Node} rangeInfos.endNode - node on which the selection ends
     * @param {Node} rangeInfos.endNodeContainer - container of the endNode, or the end node itself in case of text nodes
     * @param {number} rangeInfos.endOffset - same as range.endOffset, but not read-only to allow override
     */
    function wrapTextNodesInRange(rootNode, rangeInfos) {
        const childNodes = rootNode.childNodes;
        let currentNode, i;
        const splitDatas = [];

        for (i = 0; i < childNodes.length; i++) {
            if (hasWrapped) {
                break;
            }
            currentNode = childNodes[i];

            if (isBlacklisted(currentNode)) {
                if (isElement(currentNode)) {
                    //go deeper in case a descendant of the current blacklisted is whitelisted
                    wrapTextNodesInRange(currentNode, rangeInfos);
                }
            } else {
                const isCurrentNodeTextInsideOfAnotherHighlightingWrapper =
                    isText(currentNode) &&
                    isWrappingNode(currentNode.parentNode) &&
                    getColor(currentNode.parentNode) !== activeColor;

                if (isCurrentNodeTextInsideOfAnotherHighlightingWrapper) {
                    const internalRange = new Range();
                    internalRange.selectNodeContents(currentNode);

                    if (rangeInfos.startNode === currentNode) {
                        internalRange.setStart(currentNode, rangeInfos.startOffset);
                    }

                    if (rangeInfos.endNode === currentNode) {
                        internalRange.setEnd(currentNode, rangeInfos.endOffset);
                    }

                    const isNodeInRange = rangeInfos.commonRange.isPointInRange(currentNode, internalRange.endOffset);

                    // Apply new highlighting color only for selected nodes
                    if (isNodeInRange) {
                        isWrapping = true;
                        highlightContainerNodes(currentNode, activeColor, internalRange, currentGroupId);
                    }
                } else {
                    // split current node in case the wrapping start/ends on a partially selected text node
                    if (currentNode.isSameNode(rangeInfos.startNode)) {
                        if (isText(rangeInfos.startNodeContainer) && rangeInfos.startOffset !== 0) {
                            // we defer the wrapping to the next iteration of the loop
                            //end of node should be highlighted
                            rangeInfos.startNode = currentNode.splitText(rangeInfos.startOffset);
                            rangeInfos.startOffset = 0;
                            splitDatas.push({ node: rangeInfos.startNode, beforeWasSplit: true, afterWasSplit: false });
                        } else {
                            //whole node should be highlighted
                            isWrapping = true;
                            splitDatas.push({ node: currentNode, beforeWasSplit: false, afterWasSplit: false });
                        }
                    }

                    if (currentNode.isSameNode(rangeInfos.endNode) && isText(rangeInfos.endNodeContainer)) {
                        if (rangeInfos.endOffset !== 0) {
                            if (rangeInfos.endOffset < currentNode.textContent.length) {
                                //start of node should be highlighted
                                currentNode.splitText(rangeInfos.endOffset);
                                splitDatas.push({ node: currentNode, beforeWasSplit: false, afterWasSplit: true });
                            } else {
                                //whole node should be highlighted
                                splitDatas.push({ node: currentNode, beforeWasSplit: false, afterWasSplit: false });
                            }
                        } else {
                            isWrapping = false;
                        }
                    }

                    // wrap the current node...
                    if (isText(currentNode)) {
                        if (willHighlightNotBeEmptyAfterMerge(currentNode)) {
                            const wrapperNode = wrapTextNode(currentNode, currentGroupId);
                            if (wrapperNode) {
                                const splitData = splitDatas.find(d => d.node === currentNode);
                                addSplitData(
                                    wrapperNode,
                                    splitData ? splitData.beforeWasSplit : false,
                                    splitData ? splitData.afterWasSplit : false
                                );
                            }
                        }

                        // ... or continue deeper in the node tree
                    } else if (isElement(currentNode)) {
                        //some selections end at the very start of the next node, we should end wrapping when we reach such node
                        if (!currentNode.isSameNode(rangeInfos.endNode) || rangeInfos.endOffset > 0) {
                            wrapTextNodesInRange(currentNode, rangeInfos);
                        }
                    }
                }
            }

            // end wrapping ?
            if (currentNode.isSameNode(rangeInfos.endNode)) {
                isWrapping = false;
                hasWrapped = true;
                break;
            }
        }
    }

    /**
     * Restructure content of the highlighted wrapper according to the selectedRange
     * @param {Node} textNode
     * @param {string} color
     * @param {Range} selectedRange
     * @param {number} groupId
     */
    function highlightContainerNodes(textNode, color, selectedRange, groupId) {
        const container = textNode.parentNode;
        const range = new Range();
        range.selectNodeContents(textNode);

        const isSelectionCoversNodeStart = range.compareBoundaryPoints(Range.START_TO_START, selectedRange) === 0;
        const isSelectionCoversNodeEnd = range.compareBoundaryPoints(Range.END_TO_END, selectedRange) === 0;

        /*
        There are 4 possible cases selected area is intersected with already highlighted element.
        In examples below the border is represents the selection, "yellow" is class name of already highlighted
        container, "red" is class name of currently active highlighter
        **********************************************************************************************************
        1. The container content is completely selected, so that we only have to change the highlighter class name

        Input:
         __________________________________________________
        |                                                  |
        |<span class="yellow"> Lorem ipsum dolor sit</span>|
        |__________________________________________________|

        Output:
         <span class="red"> Lorem ipsum dolor sit</span>

        **********************************************************************************************************
        2. The container content is partially selected from the begging.

        Input:
         ______________________________
        |                              |
        |<span class="yellow"> Lorem ip|sum dolor sit</span>
        |______________________________|

        Output:
        <span class="red"> Lorem ip</span><span class="yellow">sum dolor sit</span>

        **********************************************************************************************************
        3. The container content is partially selected at the end.

        Input:
                                       ____________________
                                      |                    |
        <span class="yellow"> Lorem ip|sum dolor sit</span>|
                                      |____________________|

        Output:
        <span class="yellow"> Lorem ip</span><span class="red">sum dolor sit</span>

        **********************************************************************************************************
        4. The container content is partially selected in the middle.

        Input:
                                     ___________
                                    |           |
        <span class="yellow"> Lorem |ipsum dolor| sit</span>
                                    |___________|

        Output:
         <span class="yellow"> Lorem </span><span class="red">ipsum dolor</span><span class="yellow"> sit</span>
         */
        if (isSelectionCoversNodeStart && isSelectionCoversNodeEnd) {
            setColor(textNode.parentNode, color);
        } else if (isSelectionCoversNodeStart) {
            textNode.splitText(selectedRange.endOffset);
            wrapContainerChildNodes(container, 0, color, groupId);
        } else if (isSelectionCoversNodeEnd) {
            textNode.splitText(selectedRange.startOffset);
            wrapContainerChildNodes(container, 1, color, groupId);
        } else {
            textNode.splitText(selectedRange.startOffset).splitText(selectedRange.endOffset);
            wrapContainerChildNodes(container, 1, color, groupId);
        }
    }

    /**
     * Wraps all containers text nodes with highlighter element.
     * The child node with index given by indexToWrapNode parameter will be wrap with class given by activeClass parameter
     * @param {Element} container
     * @param {number} indexToWrapNode
     * @param {string} color
     * @param {number} groupId
     */
    function wrapContainerChildNodes(container, indexToWrapNode, color, groupId) {
        const containerColor = getColor(container);
        const fragment = new DocumentFragment();
        const childNodesLength = container.childNodes.length;

        container.childNodes.forEach((node, index) => {
            let wrapperNode;
            if (index === indexToWrapNode) {
                wrapperNode = wrapNode(node.cloneNode(), color, groupId);
            } else {
                wrapperNode = wrapNode(node.cloneNode(), containerColor, groupId);
            }
            fragment.appendChild(wrapperNode);

            addSplitData(
                wrapperNode,
                index === 0 ? container.dataset.beforeWasSplit : true,
                index === childNodesLength - 1 ? container.dataset.afterWasSplit : true
            );
        });

        container.replaceWith(fragment);
    }

    /**
     * wraps a text node into the highlight span
     * @param {Node} node - the node to wrap
     * @param {number} groupId - the highlight group
     * @returns {Node|null} wrapper node, if it was created
     */
    function wrapTextNode(node, groupId) {
        if (isWrapping && !isWrappingNode(node.parentNode) && isWrappable(node)) {
            const wrapper = getWrapper(groupId, activeColor);
            node.parentNode.insertBefore(wrapper, node);
            wrapper.appendChild(node);
            return wrapper;
        }
        return null;
    }

    /**
     * We need to re-index the groups after a user highlight: either to merge groups or to resolve inconsistencies
     * Recursive.
     *
     * @param {Node} rootNode
     */
    function reindexGroups(rootNode) {
        if (!rootNode) {
            return;
        }

        const childNodes = rootNode.childNodes;
        let i, currentNode, parent;

        for (i = 0; i < childNodes.length; i++) {
            currentNode = childNodes[i];

            if (isWrappable(currentNode)) {
                parent = currentNode.parentNode;
                if (isWrappingNode(parent)) {
                    if (isWrapping === false) {
                        currentGroupId++;
                    }
                    isWrapping = true;
                    parent.setAttribute(GROUP_ATTR, currentGroupId); // set the new group Id
                } else {
                    isWrapping = false;
                }
            } else if (isElement(currentNode)) {
                reindexGroups(currentNode);
            }
        }
    }

    /**
     * Some highlights may result in having adjacent wrapping nodes. We remove them here to get a cleaner markup.
     *
     * @param {Node} rootNode
     */
    function mergeAdjacentWrappingNodes(rootNode) {
        if (!rootNode) {
            return;
        }

        const childNodes = rootNode.childNodes;
        let i, currentNode;

        for (i = 0; i < childNodes.length; i++) {
            currentNode = childNodes[i];

            if (isWrappingNode(currentNode)) {
                currentNode.normalize();

                while (
                    isWrappingNode(currentNode.nextSibling) &&
                    getColor(currentNode) === getColor(currentNode.nextSibling)
                ) {
                    currentNode.nextSibling.normalize();

                    currentNode.firstChild.textContent += currentNode.nextSibling.firstChild.textContent;
                    addSplitData(
                        currentNode,
                        currentNode.dataset.beforeWasSplit,
                        currentNode.nextSibling.dataset.afterWasSplit
                    );
                    currentNode.parentNode.removeChild(currentNode.nextSibling);
                }
            } else if (isElement(currentNode)) {
                mergeAdjacentWrappingNodes(currentNode);
            }
        }
    }

    /**
     * Unwraps highlighted nodes with a line break or with an empty content
     */
    function unWrapEmptyHighlights() {
        getHighlightedNodes().forEach(node => {
            const nodeContent = node.textContent;

            if (nodeContent.trim().length === 0) {
                if (nodeContent.length === 0 || /\r|\n/.exec(nodeContent)) {
                    clearSingleHighlight({ target: node });
                }
            }
        });
    }

    /**
     * Check condition to avoid the work of `unwrapEmptyHighlights` ahead of time, before `mergeAdjacentNodes` runs,
     * because in `keepEmptyNodes` case we do not want to add nodes to dom unless necessary.
     * Also be more strict and don't allow to select nodes with spaces only, because they may appear in unexpected places in markup
     * (here it's not exactly same as `unwrapEmptyHighlights`).
     * @param {Node} node - node which will be wrapped (highlighted)
     * @returns {Boolean}
     */
    function willHighlightNotBeEmptyAfterMerge(node) {
        if (!node.textContent.length) {
            return false;
        }
        if (node.textContent.trim().length) {
            return true;
        }
        const prevNode = node.previousSibling;
        const canWrapperBeMergedWithPreviousSibling =
            prevNode && isWrappingNode(prevNode) && getColor(prevNode) === activeColor;
        if (canWrapperBeMergedWithPreviousSibling) {
            return true;
        }
        const nextNode = node.nextSibling;
        const canWrapperBeMergedWithNextSibling =
            nextNode && isWrappingNode(nextNode) && getColor(nextNode) === activeColor;
        if (canWrapperBeMergedWithNextSibling) {
            return true;
        }
        return false;
    }

    /**
     * `range.surroundContents` can create empty text nodes,
     * which will cause trouble in `mergeAdjacentNodes` later (in `keepEmptyNodes` case).
     * This method surrounds range, then removes those nodes
     * @param {Range} range
     * @param {Node} wrapperNode
     */
    function rangeSurroundContentsNoEmptyNodes(range, wrapperNode) {
        const containerPreviousSibling = range.commonAncestorContainer.previousSibling;
        const containerNextSibling = range.commonAncestorContainer.nextSibling;

        range.surroundContents(wrapperNode);

        removeEmptyTextNodeIfDifferent(wrapperNode.previousSibling, containerPreviousSibling);
        removeEmptyTextNodeIfDifferent(wrapperNode.nextSibling, containerNextSibling);
    }

    /**
     * Remove `node`, if it's an empty text node and is *not* the same node as `nodeToCompare`
     * @param {Node} node
     * @param {Node} nodeToCompare
     */
    function removeEmptyTextNodeIfDifferent(node, nodeToCompare) {
        if (node && node !== nodeToCompare && isText(node) && node.textContent.length === 0) {
            node.remove();
        }
    }

    /**
     * Remove all wrapping nodes from markup
     * @param {String?} color - if specified, removes nodes only for this color
     * @returns {Range[]}
     */
    function clearHighlights(color = null) {
        const highlightedNodes = color ? getHighlightedNodesForColor(color) : getHighlightedNodes();
        return highlightedNodes.map(elem => clearSingleHighlight({ target: elem }));
    }

    /**
     * Remove unwrap dom node
     * @param {Object} e
     * @returns {Range}
     */
    function clearSingleHighlight(e) {
        const nodeToRemove = e.target;
        const nodeToRemoveText = nodeToRemove.textContent;
        const beforeWasSplit = nodeToRemove.dataset.beforeWasSplit !== 'false';
        const afterWasSplit = nodeToRemove.dataset.afterWasSplit !== 'false';
        const prevNode = nodeToRemove.previousSibling;
        const nextNode = nodeToRemove.nextSibling;

        //create range covering removed highlight
        const rangeInfo = {
            node: null,
            startOffset: 0
        };

        if (beforeWasSplit && prevNode && isText(prevNode) && prevNode.textContent) {
            //append text to previous sibling
            rangeInfo.node = prevNode;
            rangeInfo.startOffset = prevNode.textContent.length;

            prevNode.textContent += nodeToRemoveText;
            nodeToRemove.remove();

            if (
                afterWasSplit &&
                prevNode.nextSibling &&
                isText(prevNode.nextSibling) &&
                prevNode.nextSibling.textContent
            ) {
                //merge it with next sibling
                prevNode.textContent += prevNode.nextSibling.textContent;
                prevNode.nextSibling.remove();
            }
        } else if (afterWasSplit && nextNode && isText(nextNode) && nextNode.textContent) {
            //append text to next sibling
            rangeInfo.node = nextNode;
            rangeInfo.startOffset = 0;

            nextNode.textContent = nodeToRemoveText + nextNode.textContent;
            nodeToRemove.remove();
        } else if (nodeToRemoveText) {
            //keep text in a separate text node
            const replacementNode = document.createTextNode(nodeToRemoveText);
            nodeToRemove.replaceWith(replacementNode);

            rangeInfo.node = replacementNode;
            rangeInfo.startOffset = 0;

            if (afterWasSplit && nextNode && isWrappingNode(nextNode)) {
                addSplitData(
                    nextNode,
                    nextNode.dataset.beforeWasSplit !== 'false' || afterWasSplit,
                    nextNode.dataset.afterWasSplit
                );
            }
            if (beforeWasSplit && prevNode && isWrappingNode(prevNode)) {
                addSplitData(
                    prevNode,
                    prevNode.dataset.beforeWasSplit,
                    prevNode.dataset.afterWasSplit !== 'false' || beforeWasSplit
                );
            }
        } else {
            //text is empty, just remove it
            nodeToRemove.remove();
        }

        const range = new Range();
        range.setStart(rangeInfo.node, rangeInfo.startOffset);
        range.setEnd(rangeInfo.node, rangeInfo.startOffset + nodeToRemoveText.length);
        return range;
    }

    /**
     * Index-related functions:
     * ========================
     * To allow saving and restoring highlights on an equivalent, but different, DOM tree (for example if the markup is deleted and re-created)
     * we build an index containing the status of each text node:
     * - not highlighted
     * - fully highlighted
     * - partially highlighted (= with inline ranges)
     *
     * This index will be used to restore a selection on the new DOM tree
     */

    /**
     * Bootstrap the process of building the highlight index
     * @returns {BuildModelResultKeepEmpty|null}
     */
    function getHighlightIndex() {
        const rootNode = getContainer();
        if (rootNode) {
            normalizeIfAllowed(rootNode);
            return buildHighlightModelKeepEmpty(rootNode);
        } else {
            return null;
        }
    }

    /**
     * Util for bulding/restoring model:
     * Loop through children of node, and invoke callback for each child
     * @param {HTMLElement} parentNode
     * @param {Function} callback - (childNode: Node, idx: Number, offset: Number) => boolean
     *    idx: If node is element, index of self among other elements. If node is text-like, index of nearest element sibling before it.
     *    offset: If node is element, 0. If node is text-like, total text length since previous element node.
     * Text-like node is a node which is text in original html without any highlights:
     *     - text node
     *     - highlight created by this highlighter
     *     - "transient" node - highlight creaated by another highlighter
     * If callback returns `false`, exit loop.
     */
    function foreachChildInModelPath(parentNode, callback) {
        let idx = -1; //among elements
        let offset = 0; //summed-up from previous element
        for (const childNode of parentNode.childNodes) {
            const isTextLike = isText(childNode) || isWrappingNode(childNode) || isTransient(childNode);
            if (!isTextLike && isElement(childNode)) {
                idx++;
                offset = 0;
            }
            const callbackResult = callback(childNode, idx, offset);
            if (callbackResult === false) {
                break;
            }
            if (isTextLike) {
                offset += childNode.textContent.length;
            }
        }
    }

    /**
     * @typedef HighlightEntryKeepEmpty
     * @property {String} groupId
     * @property {String} c - color
     * @property {Number} offsetBefore
     * @property {Number} textLength
     * @property {String} beforeWasSplit
     * @property {String} afterWasSplit
     * @property {Array<Number>} path - on each level from root container to highlight, index among siblings
     */
    /**
     * @typedef BuildModelResultKeepEmpty
     * @property {HighlightEntryKeepEmpty[]} highlightModel
     * @property {NodeList} wrapperNodes
     */
    /**
     * For `keepEmptyNodes` option, creates data model of highlights.
     * Additionally returns array of highlight nodes. Traverses DOM tree.
     * @param {Node} rootNode
     * @returns {BuildModelResultKeepEmpty|null} result
     */
    function buildHighlightModelKeepEmpty(rootNode) {
        const wrapperNodes = getHighlightedNodes();
        if (!wrapperNodes.length) {
            return null;
        }

        const highlightModel = [];
        const indexCache = new Map();
        for (let k = 0; k < wrapperNodes.length; k++) {
            const wrapperNode = wrapperNodes[k];

            const highlightData = {
                groupId: wrapperNode.getAttribute(GROUP_ATTR),
                c: getColor(wrapperNode),
                textLength: wrapperNode.textContent.length,
                offsetBefore: 0, //see below
                path2: [] //see below
            };

            //get info about its position in the tree: path through all parents from rootNode to highlight
            let currentNode = wrapperNode;
            while (currentNode && currentNode !== rootNode) {
                let offsetBefore;
                let indexInModel = indexCache.get(currentNode);
                if (!indexInModel && indexInModel !== 0) {
                    foreachChildInModelPath(currentNode.parentNode, (childNode, idx, offset) => {
                        if (childNode === currentNode) {
                            indexInModel = idx;
                            offsetBefore = offset;
                            return false;
                        }
                    });
                    indexCache.set(currentNode, indexInModel);
                }
                highlightData.path2.unshift(indexInModel);
                if (currentNode === wrapperNode) {
                    highlightData.offsetBefore = offsetBefore;
                }
                currentNode = currentNode.parentNode;
            }

            //add info about highlight and its position to model
            highlightModel.push(highlightData);
        }
        return {
            highlightModel,
            wrapperNodes
        };
    }

    /**
     * Bootstrap the process of restoring the highlights from an index
     * @param {HighlightEntryKeepEmpty[]|null} highlightIndex
     */
    function highlightFromIndex(highlightIndex) {
        const rootNode = getContainer();
        if (rootNode) {
            normalizeIfAllowed(rootNode);
            restoreHighlightKeepEmpty(rootNode, highlightIndex);
        }
    }

    /**
     * For `keepEmptyNodes` option, wraps the text nodes according to highlights data model.
     * Traverses and updates DOM tree. Shouldn't throw errors in case of mismatches.
     * @param {Node} rootNode
     * @param {HighlightEntryKeepEmpty[]|null} highlightModel
     */
    function restoreHighlightKeepEmpty(rootNode, highlightModel) {
        if (!highlightModel) {
            return;
        }

        for (const currentModel of highlightModel) {
            if (!currentModel.path2 || !currentModel.path2.length) {
                continue; //something went wrong
            }

            //find node to wrap - go through nodes until we reach level where node to wrap will be
            let lastParentNode = rootNode;
            for (let k = 0; k < currentModel.path2.length; k++) {
                const lastLevelInPath = k === currentModel.path2.length - 1;
                const modelOffsetBefore = currentModel.offsetBefore;
                const modelTextLength = currentModel.textLength;
                const modelPathIndex = currentModel.path2[k];

                const relevantNodes = [];
                let pathNode;
                foreachChildInModelPath(lastParentNode, (childNode, idx, offset) => {
                    if (idx === modelPathIndex) {
                        if (!pathNode) {
                            pathNode = childNode;
                        }
                        if (
                            lastLevelInPath &&
                            isText(childNode) &&
                            childNode.textContent.length &&
                            modelOffsetBefore + modelTextLength > offset &&
                            modelOffsetBefore < offset + childNode.textContent.length &&
                            !isBlacklisted(childNode)
                        ) {
                            relevantNodes.push([childNode, offset]);
                        }
                    }
                    if (idx > modelPathIndex) {
                        return false;
                    }
                });

                if (!lastLevelInPath) {
                    lastParentNode = pathNode;
                    if (!lastParentNode || !isElement(lastParentNode)) {
                        //something went wrong
                        break;
                    }
                } else {
                    for (let j = 0; j < relevantNodes.length; j++) {
                        const relNode = relevantNodes[j][0];
                        const previousNodesTotalLength = relevantNodes[j][1];
                        const relNodeLength = relNode.textContent.length;
                        const relOffsetBefore = modelOffsetBefore - previousNodesTotalLength;
                        const relTextLength = relOffsetBefore + modelTextLength;

                        //highlight it
                        let nodeToHighlight = relNode;
                        let beforeWasSplit = false;
                        let afterWasSplit = false;
                        if (relOffsetBefore > 0) {
                            nodeToHighlight = nodeToHighlight.splitText(relOffsetBefore);
                            beforeWasSplit = true;
                        }
                        if (relTextLength < relNodeLength) {
                            nodeToHighlight.splitText(relTextLength - Math.max(relOffsetBefore, 0));
                            afterWasSplit = true;
                        }

                        const wrapperNode = getWrapper(currentModel.groupId, currentModel.c);
                        const range = document.createRange();
                        range.selectNodeContents(nodeToHighlight);
                        rangeSurroundContentsNoEmptyNodes(range, wrapperNode);
                        addSplitData(wrapperNode, beforeWasSplit, afterWasSplit);
                        //assume "isTransient" nodes exist only on normalized ares where keepEmptyNodes=false!
                        //  otherwise we should have taken there beforeWasSplit/afterWasSplit into account
                    }
                }
            }
        }
    }

    /**
     * Set highlighter color
     * @param {string} color Active highlighter color
     */
    function setActiveColor(color) {
        activeColor = color;
    }

    /**
     * Returns color identifier for the given node
     * @param {Node} wrapperNode
     * @returns {string} Color identifier
     */
    function getColor(wrapperNode) {
        return wrapperNode.dataset.color;
    }

    /**
     * Sets color identifier for the given node
     * @param {Node} wrapperNode
     * @param {string} color
     */
    function setColor(wrapperNode, color) {
        wrapperNode.dataset.color = color || '';
    }

    /**
     * Returns color identifier for the given node
     * @param {Node} node
     * @returns {string|number|null} Color identifier
     */
    function getColorKeyForHighlight(node) {
        if (isWrappingNode(node)) {
            return getColor(node);
        }
        return null;
    }

    /**
     * Check if the given node is a wrapper
     * @param {Node|Element} node
     * @returns {boolean}
     */
    function isWrappingNode(node) {
        return isElement(node) && node.tagName.toLowerCase() === 'span' && node.classList.contains(baseClassName);
    }

    /**
     * Check if the given node can be wrapped
     * @param {Node} node
     * @returns {boolean}
     */
    function isWrappable(node) {
        return isText(node) && !isBlacklisted(node);
    }

    /**
     * Check if the given node is, or is within, a blacklisted container.
     * With `keepEmptyNodes` option, node inside blacklisted container can be whitelisted too.
     * Priority of blacklist or whitelist is decided by which selector is closest to the node.
     * If no match found, node is considered whitelisted.
     * @param {Node} node
     * @returns {boolean}
     */
    function isBlacklisted(node) {
        let element = isElement(node) ? node : node.parentElement;
        const closest = element.closest(containersBlackAndWhiteListSelector);
        if (!closest) {
            return false;
        } else if (!containersWhiteListSelector) {
            return true;
        } else {
            return !closest.matches(containersWhiteListSelector);
        }
    }

    /**
     * Wraps text node to the highlighter wrapper element
     * @param {Node} textNode Text node to wrap
     * @param {string} color
     * @param {number} groupId Group id
     * @returns {Node}
     */
    function wrapNode(textNode, color, groupId) {
        const element = getWrapper(groupId, color);
        element.appendChild(textNode);
        return element;
    }

    /**
     * Create a wrapping node
     * @param {number} groupId
     * @param {string} color
     * @returns {Element}
     */
    function getWrapper(groupId, color) {
        const wrapper = document.createElement('span');
        wrapper.classList.add(baseClassName);
        setColor(wrapper, color);
        wrapper.setAttribute(GROUP_ATTR, `${groupId}`);
        return wrapper;
    }

    /**
     * Returns the first unused group Id
     * @returns {number}
     */
    function getAvailableGroupId() {
        let id = currentGroupId || 1;
        while (getContainer().querySelector(`[${GROUP_ATTR}="${id}"]`)) {
            id++;
        }
        return id;
    }

    /**
     * Check if the given node is an element
     * @param {Node} node
     * @returns {boolean}
     */
    function isElement(node) {
        return node && typeof node === 'object' && node.nodeType === window.Node.ELEMENT_NODE;
    }

    /**
     * Check if the given node is of type text
     * @param {Node} node
     * @returns {boolean}
     */
    function isText(node) {
        return node && typeof node === 'object' && node.nodeType === window.Node.TEXT_NODE;
    }

    /**
     * Check if the given node is "transient"
     * (created by another highlighter and shouldn't be included in the model)
     * @param {Node} node
     * @returns {boolean}
     */
    function isTransient(node) {
        return options.transientSelector && isElement(node) && node.matches(options.transientSelector);
    }

    /**
     * Normalize node (merge text nodes & remove empty text nodes),
     * if node can be normalized according to config options
     * @param {Node} node
     */
    function normalizeIfAllowed(node) {
        if (isElement(node)) {
            if (!keepEmptyNodes) {
                node.normalize();
            } else if (keepEmptyNodesIgnoreSelector) {
                if (node.closest(keepEmptyNodesIgnoreSelector)) {
                    node.normalize();
                } else {
                    node.querySelectorAll(keepEmptyNodesIgnoreSelector).forEach(normalizableNode =>
                        normalizableNode.normalize()
                    );
                }
            }
        }
    }

    /**
     * Split data is relevant only if we should keep empty nodes inside this node.
     * Ensure `node` is already attached to the DOM, otherwise `keepEmptyNodesIgnoreSelector` check won't return correct result
     * @param {Element} node
     * @returns {boolean}
     */
    function shouldAddSplitData(node) {
        if (!keepEmptyNodes) {
            return false;
        } else if (keepEmptyNodesIgnoreSelector) {
            return !node.closest(keepEmptyNodesIgnoreSelector);
        }
        return true;
    }

    /**
     * Public API of the highlighter helper
     */
    return {
        highlightRanges: highlightRanges,
        highlightFromIndex: highlightFromIndex,
        getHighlightIndex: getHighlightIndex,
        clearHighlights: clearHighlights,
        clearSingleHighlight: clearSingleHighlight,
        getColorKeyForHighlight: getColorKeyForHighlight,
        setActiveColor
    };
}
